// web/src/screens/CartScreen.jsx
import { useState, useEffect } from 'react';
import { useNav } from '../App';
import { apiClient } from '../api';

const C = { bg:'var(--bg)', surface:'var(--surface)', orange:'var(--orange)', text:'var(--text)', muted:'var(--text-muted)' };

const PAYMENT_METHOD_LABEL = {
  cash:         'Cash',
  mpesa_manual: 'M-Pesa (Manual)',
  mpesa_stk:    'M-Pesa (STK)',
  card:         'Card',
  tab:          'Tab',
  complimentary:'Complimentary',
};

export default function CartScreen() {
  const { params, navigate }        = useNav();
  const { table, ticketId }         = params;
  const [lines, setLines]           = useState(params.lines || []);
  const [total, setTotal]           = useState(params.total || 0);
  const [balanceDue, setBalanceDue] = useState(params.total || 0);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [payMethod, setPayMethod]   = useState(null);
  const [payAmount, setPayAmount]   = useState('');
  const [mpesaCode, setMpesaCode]   = useState('');
  const [mpesaPhone, setMpesaPhone] = useState('');
  const [custPhone, setCustPhone]   = useState('');
  const [cashReceived, setCashReceived] = useState('');
  const [processing, setProcessing] = useState(false);
  const [stkSent, setStkSent]       = useState(false);
  const [stkPollInterval, setStkPollInterval] = useState(null);

  const VAT_RATE = 0.16;
  const subtotal = parseFloat(total) / (1 + VAT_RATE);
  const vat      = parseFloat(total) - subtotal;

  const payAmountNum = parseFloat(payAmount);
  const hasPayAmount = payAmount.trim() !== '' && !isNaN(payAmountNum) && payAmountNum > 0;

  const cashReceivedNum = parseFloat(cashReceived);
  const hasCashReceived = cashReceived.trim() !== '' && !isNaN(cashReceivedNum);
  const changeDue = hasCashReceived && hasPayAmount
    ? Math.round((cashReceivedNum - payAmountNum) * 100) / 100
    : null;

  // Selecting a payment method always starts from the current remaining
  // balance — editable down for a genuine split payment.
  const selectMethod = (method) => {
    setPayMethod(method);
    setPayAmount(balanceDue ? String(balanceDue) : '');
    if (method === 'cash') setCashReceived(balanceDue ? String(balanceDue) : '');
  };

  // Fetch fresh ticket data on mount
  useEffect(() => {
    const loadTicket = async () => {
      try {
        const { data } = await apiClient.get(`/tickets/${ticketId}`);
        setLines(data.lines || []);
        setTotal(parseFloat(data.total) || 0);
        setBalanceDue(parseFloat(data.balance_due) || 0);
        setPaymentHistory(data.payments || []);
      } catch {
        // use params data as fallback
      }
    };
    if (ticketId) loadTicket();
  }, [ticketId]);

  // Poll for STK confirmation. A partial STK payment closes neither the
  // ticket nor the table, so watch the ticket's own balance_due rather than
  // waiting for the table to go free — that only happens once fully paid.
  useEffect(() => {
    if (stkSent) {
      const interval = setInterval(async () => {
        try {
          const { data } = await apiClient.get(`/tickets/${ticketId}`);
          const newBalance = parseFloat(data.balance_due) || 0;
          if (newBalance < balanceDue) {
            clearInterval(interval);
            setStkSent(false);
            setPayMethod(null);
            setPaymentHistory(data.payments || []);
            if (newBalance <= 0) {
              navigate('tables');
            } else {
              setBalanceDue(newBalance);
            }
          }
        } catch {}
      }, 3000);
      setStkPollInterval(interval);
      return () => clearInterval(interval);
    }
  }, [stkSent]);

  // Format phone — ensure 254 prefix, no +
  const formatPhone = (phone) => {
    let p = phone.trim().replace(/\s/g, '');
    if (p.startsWith('+')) p = p.slice(1);
    if (p.startsWith('0'))  p = '254' + p.slice(1);
    if (!p.startsWith('254')) p = '254' + p;
    return p;
  };

  // A pay-manual response tells us the new remaining balance. Zero (or
  // below) means the ticket actually closed server-side — leave for
  // Tables. Otherwise stay here: reset to the method picker with the
  // updated balance so a different method can cover the rest.
  const applyPaymentResult = (data, method, amount) => {
    const remaining = Number(data.remaining_balance);
    setPaymentHistory(prev => [...prev, { method, amount, status: 'confirmed' }]);
    if (remaining <= 0) {
      navigate('tables');
    } else {
      setBalanceDue(remaining);
      setPayMethod(null);
    }
  };

  const handleConfirmCash = async () => {
    if (!hasPayAmount) return alert('Enter an amount to charge');
    setProcessing(true);
    try {
      const { data } = await apiClient.post(`/payments/tickets/${ticketId}/pay-manual`, {
        method:        'cash',
        amount:        payAmountNum,
        cash_received: hasCashReceived ? cashReceivedNum : undefined,
      });
      applyPaymentResult(data, 'cash', payAmountNum);
    } catch (err) {
      alert(err.data?.error || 'Payment failed. Try again.');
    } finally { setProcessing(false); }
  };

  const handleManualMpesa = async () => {
    if (!hasPayAmount) return alert('Enter an amount to charge');
    setProcessing(true);
    try {
      const { data } = await apiClient.post(`/payments/tickets/${ticketId}/pay-manual`, {
        method:      'mpesa_manual',
        amount:      payAmountNum,
        mpesa_code:  mpesaCode  || undefined,
        mpesa_phone: mpesaPhone ? formatPhone(mpesaPhone) : undefined,
      });
      applyPaymentResult(data, 'mpesa_manual', payAmountNum);
    } catch (err) {
      alert(err.data?.error || 'Payment failed. Try again.');
    } finally { setProcessing(false); }
  };

  const handleStkPush = async () => {
    if (!hasPayAmount) return alert('Enter an amount to charge');
    if (!custPhone.trim()) return alert('Enter customer phone number');
    const phone = formatPhone(custPhone);
    if (phone.length < 12) return alert('Enter a valid phone number e.g. 0712345678');

    setProcessing(true);
    try {
      await apiClient.post(`/payments/tickets/${ticketId}/request-mpesa-stk`, {
        phone,
        amount: payAmountNum,
      });
      setStkSent(true);
    } catch (err) {
      alert(err.data?.error || 'STK Push failed. Try manual M-Pesa instead.');
    } finally { setProcessing(false); }
  };

  return (
    <div style={s.container}>
      <div style={s.header}>
        <button style={s.backBtn} onClick={() => navigate('order', params)}>← BACK</button>
        <span style={s.title}>BILL — {table?.name}</span>
        <span />
      </div>

      {/* Items summary */}
      <div style={s.section}>
        {lines.filter(l => !l.voided).map(l => (
          <div key={l.id} style={s.lineRow}>
            <span style={s.lineQtyName}>{l.qty}× {l.name}</span>
            <span style={s.lineAmt}>
              KES {(parseFloat(l.price) * l.qty).toLocaleString()}
            </span>
          </div>
        ))}
        <div style={s.divider} />
        <div style={s.lineRow}>
          <span style={{ color: C.muted }}>Subtotal</span>
          <span style={{ color: C.muted }}>KES {subtotal.toFixed(0)}</span>
        </div>
        <div style={s.lineRow}>
          <span style={{ color: C.muted }}>VAT 16%</span>
          <span style={{ color: C.muted }}>KES {vat.toFixed(0)}</span>
        </div>
        <div style={{ ...s.lineRow, marginTop: 8 }}>
          <span style={s.totalLabel}>TOTAL</span>
          <span style={s.totalAmt}>KES {parseFloat(total).toLocaleString()}</span>
        </div>
        {balanceDue < total && (
          <div style={s.lineRow}>
            <span style={s.balanceLabel}>BALANCE OWED</span>
            <span style={s.balanceAmt}>KES {Number(balanceDue).toLocaleString()}</span>
          </div>
        )}
      </div>

      {/* Prior payments on this ticket, if any */}
      {paymentHistory.length > 0 && !payMethod && !stkSent && (
        <div style={s.section}>
          <p style={s.sectionLabel}>PAYMENTS SO FAR</p>
          {paymentHistory.map((p, i) => (
            <div key={i} style={s.lineRow}>
              <span style={{ color: C.muted, fontSize: 13 }}>{PAYMENT_METHOD_LABEL[p.method] || p.method}</span>
              <span style={{ color: 'var(--text)', fontSize: 13, fontWeight: 600 }}>
                KES {Number(p.amount).toLocaleString()}
              </span>
            </div>
          ))}
          <div style={{ ...s.lineRow, marginTop: 6 }}>
            <span style={s.balanceLabel}>BALANCE REMAINING</span>
            <span style={s.balanceAmt}>KES {Number(balanceDue).toLocaleString()}</span>
          </div>
        </div>
      )}

      {/* Payment options */}
      {!payMethod && !stkSent && (
        <div style={s.section}>
          <p style={s.sectionLabel}>SELECT PAYMENT METHOD</p>
          <button style={s.payBtn} onClick={() => selectMethod('stk')}>
            💳 SEND M-PESA REQUEST
          </button>
          <button style={s.payBtn} onClick={() => selectMethod('manual')}>
            📝 CUSTOMER ALREADY PAID (M-PESA)
          </button>
          <button style={{ ...s.payBtn, background:'var(--success)' }}
            onClick={() => selectMethod('cash')}>
            💵 CASH
          </button>
        </div>
      )}

      {/* Cash tender form */}
      {payMethod === 'cash' && (
        <div style={s.section}>
          <p style={s.sectionLabel}>CASH PAYMENT</p>
          <p style={{ color: C.muted, fontSize: 12, marginBottom: 10 }}>
            Balance owed: <strong style={{ color: 'var(--text)' }}>KES {Number(balanceDue).toLocaleString()}</strong>
          </p>
          <input
            style={s.input}
            type="number"
            inputMode="decimal"
            placeholder="Amount to charge (defaults to full balance)"
            value={payAmount}
            onChange={e => setPayAmount(e.target.value)}
          />
          <input
            style={s.input}
            type="number"
            inputMode="decimal"
            placeholder="Amount received"
            value={cashReceived}
            onChange={e => setCashReceived(e.target.value)}
          />
          <div style={s.presetRow}>
            {[50, 100, 200, 500, 1000, 2000].map(v => (
              <button key={v} style={s.presetBtn} onClick={() => setCashReceived(String(v))}>
                {v}
              </button>
            ))}
          </div>
          {hasCashReceived && (
            changeDue >= 0 ? (
              <p style={s.changeDue}>Change due: KES {changeDue.toLocaleString()}</p>
            ) : (
              <>
                <p style={s.changeShort}>Short by KES {Math.abs(changeDue).toLocaleString()}</p>
                <p style={s.hint}>⚠️ Only the received amount will be applied — the rest stays as balance owed</p>
              </>
            )
          )}
          <button style={s.confirmBtn} onClick={handleConfirmCash} disabled={processing}>
            {processing ? 'CONFIRMING...' : '✓ CONFIRM CASH PAYMENT'}
          </button>
          <button style={s.cancelLink} onClick={() => setPayMethod(null)}>Cancel</button>
        </div>
      )}

      {/* STK Push form */}
      {payMethod === 'stk' && !stkSent && (
        <div style={s.section}>
          <p style={s.sectionLabel}>SEND M-PESA REQUEST TO CUSTOMER</p>
          <p style={{ color: C.muted, fontSize: 12, marginBottom: 10 }}>
            Balance owed: <strong style={{ color: 'var(--text)' }}>KES {Number(balanceDue).toLocaleString()}</strong>
          </p>
          <input
            style={s.input}
            type="number"
            inputMode="decimal"
            placeholder="Amount to charge (defaults to full balance)"
            value={payAmount}
            onChange={e => setPayAmount(e.target.value)}
          />
          <input
            style={s.input}
            type="tel"
            placeholder="Customer phone e.g. 0712345678"
            value={custPhone}
            onChange={e => setCustPhone(e.target.value)}
          />
          <button style={s.confirmBtn} onClick={handleStkPush} disabled={processing}>
            {processing ? 'SENDING...' : 'SEND M-PESA REQUEST'}
          </button>
          <button style={s.cancelLink} onClick={() => setPayMethod(null)}>Cancel</button>
        </div>
      )}

      {/* STK awaiting confirmation */}
      {stkSent && (
        <div style={{ ...s.section, textAlign: 'center' }}>
          <p style={{ color: C.orange, fontSize: 22, fontWeight: 700, marginBottom: 8 }}>
            📱 Prompt Sent!
          </p>
          <p style={{ color: C.muted, fontSize: 13, marginBottom: 24, lineHeight: 1.5 }}>
            Customer should see the M-Pesa prompt on their phone.{'\n'}
            The table updates automatically once payment is confirmed —
            fully if this settles the balance, or with the new remaining
            balance if it doesn't.
          </p>
          <div style={s.waitingDot} />
          <p style={{ color: 'var(--text-dim)', fontSize: 12, marginTop: 12, marginBottom: 24 }}>
            Waiting for confirmation...
          </p>
          <button style={{ ...s.payBtn, textAlign: 'center' }} onClick={() => navigate('tables')}>
            ← BACK TO TABLES
          </button>
          <button style={{ ...s.cancelLink, marginTop: 8 }}
            onClick={() => { setPayMethod('manual'); setStkSent(false); }}>
            Enter code manually instead
          </button>
        </div>
      )}

      {/* Manual M-Pesa form */}
      {payMethod === 'manual' && (
        <div style={s.section}>
          <p style={s.sectionLabel}>RECORD M-PESA PAYMENT</p>
          <p style={{ color: C.muted, fontSize: 12, marginBottom: 10 }}>
            Balance owed: <strong style={{ color: 'var(--text)' }}>KES {Number(balanceDue).toLocaleString()}</strong>
          </p>
          <input
            style={s.input}
            type="number"
            inputMode="decimal"
            placeholder="Amount to charge (defaults to full balance)"
            value={payAmount}
            onChange={e => setPayAmount(e.target.value)}
          />
          <input
            style={s.input}
            placeholder="M-Pesa code (optional — e.g. QK7X8Y2Z1)"
            value={mpesaCode}
            onChange={e => setMpesaCode(e.target.value.toUpperCase())}
          />
          <input
            style={s.input}
            type="tel"
            placeholder="Customer phone (optional)"
            value={mpesaPhone}
            onChange={e => setMpesaPhone(e.target.value)}
          />
          <p style={s.hint}>💡 Code is optional — never block a payment for a missing code</p>
          <button style={s.confirmBtn} onClick={handleManualMpesa} disabled={processing}>
            {processing ? 'CONFIRMING...' : '✓ CONFIRM PAYMENT'}
          </button>
          <button style={s.cancelLink} onClick={() => setPayMethod(null)}>Cancel</button>
        </div>
      )}
    </div>
  );
}

const s = {
  container:    { backgroundColor:'var(--bg)', minHeight:'100vh', color:'var(--text)', fontFamily:"'Barlow Condensed',sans-serif" },
  header:       { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 16px', borderBottom:'1px solid var(--border)' },
  backBtn:      { background:'none', border:'none', color:'var(--orange)', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:'inherit' },
  title:        { fontSize:18, fontWeight:700, letterSpacing:2 },
  section:      { padding:16, borderBottom:'1px solid var(--border)' },
  lineRow:      { display:'flex', justifyContent:'space-between', marginBottom:6 },
  lineQtyName:  { color:'var(--text)', fontSize:14 },
  lineAmt:      { color:'var(--text)', fontSize:14, fontWeight:600 },
  divider:      { borderTop:'1px solid var(--border)', margin:'12px 0' },
  totalLabel:   { color:'var(--orange)', fontSize:20, fontWeight:700 },
  totalAmt:     { color:'var(--orange)', fontSize:20, fontWeight:700 },
  balanceLabel: { color:'var(--table-bill)', fontSize:13, fontWeight:700, letterSpacing:1 },
  balanceAmt:   { color:'var(--table-bill)', fontSize:16, fontWeight:700 },
  sectionLabel: { color:'var(--text-muted)', fontSize:11, letterSpacing:2, marginBottom:12 },
  payBtn:       { display:'block', width:'100%', padding:14, background:'var(--surface-2)', border:'1px solid var(--border)',
                   borderRadius:8, color:'var(--text)', fontSize:15, fontWeight:700, cursor:'pointer',
                   marginBottom:8, fontFamily:'inherit', textAlign:'left' },
  input:        { display:'block', width:'100%', padding:12, background:'var(--surface-2)', border:'1px solid var(--border)',
                   borderRadius:8, color:'var(--text)', fontSize:15, marginBottom:10,
                   boxSizing:'border-box', fontFamily:'inherit' },
  hint:         { color:'var(--text-muted)', fontSize:12, marginBottom:12 },
  presetRow:    { display:'flex', flexWrap:'wrap', gap:8, marginBottom:12 },
  presetBtn:    { padding:'8px 14px', background:'var(--surface-2)', border:'1px solid var(--border)',
                   borderRadius:6, color:'var(--text)', fontSize:13, fontWeight:700, cursor:'pointer',
                   fontFamily:'inherit' },
  changeDue:    { color:'var(--success)', fontSize:20, fontWeight:700, marginBottom:12 },
  changeShort:  { color:'var(--orange)', fontSize:20, fontWeight:700, marginBottom:4 },
  confirmBtn:   { width:'100%', padding:14, background:'var(--orange)', border:'none', borderRadius:8,
                   color:'#fff', fontSize:16, fontWeight:700, cursor:'pointer', fontFamily:'inherit', letterSpacing:1 },
  cancelLink:   { display:'block', textAlign:'center', color:'var(--text-muted)', fontSize:13,
                   background:'none', border:'none', cursor:'pointer', marginTop:12, fontFamily:'inherit' },
  waitingDot:   { width:12, height:12, borderRadius:'50%', background:'var(--orange)',
                   margin:'0 auto', animation:'pulse 1.5s infinite' },
};
